import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';
import { Switch } from './ui/switch';
import {
  BarChart3,
  Target,
  TrendingUp,
  Users,
  DollarSign,
  Megaphone,
  Calendar,
  FileText,
  Lightbulb,
  MessageSquare,
  Zap,
  Play,
  Pause,
  Eye,
  ArrowUpRight,
  ArrowDownRight,
  Plus,
  Edit,
  Save,
  CheckCircle2,
  XCircle,
  Clock,
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#f56258'];

// Datos de métricas
const metricsData = {
  leadsMonth: 45,
  costPerLead: 55.5,
  bestChannel: 'Meta Ads',
  conversionRate: 18.5,
  monthlyGrowth: 12.3,
};

const channelData = [
  { name: 'Meta Ads', leads: 22, cost: 1100, conversion: 22 },
  { name: 'Google Ads', leads: 12, cost: 720, conversion: 15 },
  { name: 'LinkedIn', leads: 6, cost: 480, conversion: 12 },
  { name: 'Orgánico', leads: 5, cost: 0, conversion: 25 },
];

const growthData = [
  { month: 'Sep', leads: 28 },
  { month: 'Oct', leads: 32 },
  { month: 'Nov', leads: 38 },
  { month: 'Dic', leads: 41 },
  { month: 'Ene', leads: 45 },
];

// Datos de posicionamiento
const positioningData = {
  propuestaValor: 'Ayudamos a agencias y empresas de servicios a escalar su facturación con sistemas de marketing y ventas probados.',
  nichoPrioritario: 'Agencias de marketing y consultoras B2B',
  mensajePrincipal: 'Escala tu agencia sin perder la cordura',
  diferenciador: 'Combinamos estrategia, ejecución y tecnología en un solo equipo',
  promesa: 'Duplica tu facturación en 12 meses con clientes que pagan lo que vales',
  objeciones: [
    { objecion: 'Es muy caro', respuesta: 'El ROI promedio de nuestros clientes es 5x en 6 meses' },
    { objecion: 'Ya lo intenté antes', respuesta: 'Nuestro sistema está probado con +50 agencias' },
    { objecion: 'No tengo tiempo', respuesta: 'Nosotros ejecutamos, tú solo apruebas' },
  ],
  pruebaSocial: [
    'Tech Solutions: +180% facturación en 8 meses',
    'StartupX: De 3 a 12 clientes en 6 meses',
    '50+ agencias han trabajado con nosotros',
  ],
};

// Datos de contenido
const contentPillars = [
  { name: 'Educativo', percentage: 40, color: 'bg-blue-500' },
  { name: 'Casos de Éxito', percentage: 25, color: 'bg-emerald-500' },
  { name: 'Behind the Scenes', percentage: 20, color: 'bg-purple-500' },
  { name: 'Entretenimiento', percentage: 15, color: 'bg-amber-500' },
];

const leadMagnets = [
  { name: 'Ebook: 10 Estrategias de Captación', status: 'activo', downloads: 234 },
  { name: 'Masterclass: Ventas para Agencias', status: 'activo', downloads: 156 },
  { name: 'Template: Propuesta de Servicios', status: 'activo', downloads: 89 },
  { name: 'Checklist: Auditoría Marketing', status: 'inactivo', downloads: 45 },
];

// Datos de campañas
const activeCampaigns = [
  { name: 'Captación Agencias Q1', platform: 'Meta Ads', budget: 2000, spent: 1450, leads: 28, status: 'activa' },
  { name: 'Retargeting Web', platform: 'Meta Ads', budget: 500, spent: 320, leads: 8, status: 'activa' },
  { name: 'Lead Magnet Ebook', platform: 'Google Ads', budget: 800, spent: 560, leads: 12, status: 'activa' },
];

const campaignHistory = [
  { name: 'Black Friday 2025', platform: 'Meta Ads', budget: 3000, spent: 2850, leads: 65, roi: 4.2 },
  { name: 'Lanzamiento Servicio', platform: 'Multi', budget: 5000, spent: 4800, leads: 95, roi: 3.8 },
  { name: 'Webinar Diciembre', platform: 'Meta Ads', budget: 1500, spent: 1500, leads: 42, roi: 5.1 },
];

export function MarketingPage() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [editingPositioning, setEditingPositioning] = useState(false);
  const [positioning, setPositioning] = useState(positioningData);

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
      data-testid="marketing-page"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Marketing</h1>
          <p className="text-muted-foreground mt-1">Métricas, estrategia y campañas</p>
        </div>
        <Badge variant="outline" className="text-primary border-primary">
          <Calendar className="w-3 h-3 mr-1" />
          Enero 2026
        </Badge>
      </motion.div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid grid-cols-5 w-full max-w-2xl">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="posicionamiento">Posicionamiento</TabsTrigger>
          <TabsTrigger value="estrategia">Estrategia</TabsTrigger>
          <TabsTrigger value="contenido">Contenido</TabsTrigger>
          <TabsTrigger value="ads">Ads</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* KPIs */}
          <motion.div variants={itemVariants} className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="card-hover kpi-emerald">
              <CardContent className="p-4 text-center">
                <Users className="w-6 h-6 mx-auto text-emerald-600 mb-2" />
                <p className="text-xs text-muted-foreground">Leads Mes</p>
                <p className="text-2xl font-bold">{metricsData.leadsMonth}</p>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <ArrowUpRight className="w-3 h-3 text-emerald-500" />
                  <span className="text-xs text-emerald-500">+{metricsData.monthlyGrowth}%</span>
                </div>
              </CardContent>
            </Card>
            <Card className="card-hover kpi-blue">
              <CardContent className="p-4 text-center">
                <DollarSign className="w-6 h-6 mx-auto text-blue-600 mb-2" />
                <p className="text-xs text-muted-foreground">Coste/Lead</p>
                <p className="text-2xl font-bold">€{metricsData.costPerLead}</p>
              </CardContent>
            </Card>
            <Card className="card-hover kpi-amber">
              <CardContent className="p-4 text-center">
                <Megaphone className="w-6 h-6 mx-auto text-amber-600 mb-2" />
                <p className="text-xs text-muted-foreground">Canal Top</p>
                <p className="text-lg font-bold">{metricsData.bestChannel}</p>
              </CardContent>
            </Card>
            <Card className="card-hover kpi-coral">
              <CardContent className="p-4 text-center">
                <Target className="w-6 h-6 mx-auto text-red-500 mb-2" />
                <p className="text-xs text-muted-foreground">% Conversión</p>
                <p className="text-2xl font-bold">{metricsData.conversionRate}%</p>
              </CardContent>
            </Card>
            <Card className="card-hover bg-gradient-to-br from-emerald-50 to-blue-50">
              <CardContent className="p-4 text-center">
                <TrendingUp className="w-6 h-6 mx-auto text-primary mb-2" />
                <p className="text-xs text-muted-foreground">Crecimiento</p>
                <p className="text-2xl font-bold text-primary">+{metricsData.monthlyGrowth}%</p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <motion.div variants={itemVariants}>
              <Card className="card-hover">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-primary" />
                    Leads por Canal
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={channelData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="name" fontSize={12} />
                        <YAxis fontSize={12} />
                        <Tooltip />
                        <Bar dataKey="leads" fill="#10b981" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Card className="card-hover">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-primary" />
                    Crecimiento Mensual
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={growthData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="month" fontSize={12} />
                        <YAxis fontSize={12} />
                        <Tooltip />
                        <Line type="monotone" dataKey="leads" stroke="#10b981" strokeWidth={3} dot={{ fill: '#10b981' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Channel Performance Table */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <CardTitle>Rendimiento por Canal</CardTitle>
              </CardHeader>
              <CardContent>
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 text-sm font-medium">Canal</th>
                      <th className="text-right py-2 text-sm font-medium">Leads</th>
                      <th className="text-right py-2 text-sm font-medium">Inversión</th>
                      <th className="text-right py-2 text-sm font-medium">Coste/Lead</th>
                      <th className="text-right py-2 text-sm font-medium">% Conversión</th>
                    </tr>
                  </thead>
                  <tbody>
                    {channelData.map((channel, index) => (
                      <tr key={index} className="border-b hover:bg-muted/30">
                        <td className="py-3 font-medium">{channel.name}</td>
                        <td className="py-3 text-right">{channel.leads}</td>
                        <td className="py-3 text-right">€{channel.cost.toLocaleString()}</td>
                        <td className="py-3 text-right">€{channel.cost > 0 ? (channel.cost / channel.leads).toFixed(2) : '0'}</td>
                        <td className="py-3 text-right">
                          <Badge className={channel.conversion >= 20 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                            {channel.conversion}%
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        {/* Posicionamiento Tab */}
        <TabsContent value="posicionamiento" className="space-y-6">
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-primary" />
                      Posicionamiento de Marca
                    </CardTitle>
                    <CardDescription>Define tu propuesta de valor y diferenciación</CardDescription>
                  </div>
                  <Button variant="outline" onClick={() => setEditingPositioning(!editingPositioning)}>
                    {editingPositioning ? <Save className="w-4 h-4 mr-1" /> : <Edit className="w-4 h-4 mr-1" />}
                    {editingPositioning ? 'Guardar' : 'Editar'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                      <Label className="text-emerald-700 font-semibold">Propuesta de Valor</Label>
                      {editingPositioning ? (
                        <Textarea value={positioning.propuestaValor} onChange={(e) => setPositioning({...positioning, propuestaValor: e.target.value})} className="mt-2" rows={3} />
                      ) : (
                        <p className="mt-2 text-emerald-800">{positioning.propuestaValor}</p>
                      )}
                    </div>
                    <div className="p-4 rounded-xl bg-blue-50 border border-blue-100">
                      <Label className="text-blue-700 font-semibold">Nicho Prioritario</Label>
                      {editingPositioning ? (
                        <Input value={positioning.nichoPrioritario} onChange={(e) => setPositioning({...positioning, nichoPrioritario: e.target.value})} className="mt-2" />
                      ) : (
                        <p className="mt-2 text-blue-800">{positioning.nichoPrioritario}</p>
                      )}
                    </div>
                    <div className="p-4 rounded-xl bg-purple-50 border border-purple-100">
                      <Label className="text-purple-700 font-semibold">Mensaje Principal</Label>
                      {editingPositioning ? (
                        <Input value={positioning.mensajePrincipal} onChange={(e) => setPositioning({...positioning, mensajePrincipal: e.target.value})} className="mt-2" />
                      ) : (
                        <p className="mt-2 text-purple-800 text-lg font-medium">"{positioning.mensajePrincipal}"</p>
                      )}
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="p-4 rounded-xl bg-amber-50 border border-amber-100">
                      <Label className="text-amber-700 font-semibold">Diferenciador</Label>
                      {editingPositioning ? (
                        <Textarea value={positioning.diferenciador} onChange={(e) => setPositioning({...positioning, diferenciador: e.target.value})} className="mt-2" rows={2} />
                      ) : (
                        <p className="mt-2 text-amber-800">{positioning.diferenciador}</p>
                      )}
                    </div>
                    <div className="p-4 rounded-xl bg-red-50 border border-red-100">
                      <Label className="text-red-700 font-semibold">Promesa</Label>
                      {editingPositioning ? (
                        <Textarea value={positioning.promesa} onChange={(e) => setPositioning({...positioning, promesa: e.target.value})} className="mt-2" rows={2} />
                      ) : (
                        <p className="mt-2 text-red-800 font-medium">{positioning.promesa}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Objeciones */}
                <div className="p-4 rounded-xl bg-muted/30">
                  <Label className="font-semibold mb-3 block">Objeciones Habituales y Respuestas</Label>
                  <div className="space-y-3">
                    {positioning.objeciones.map((item, index) => (
                      <div key={index} className="flex gap-4 p-3 bg-white rounded-lg border">
                        <div className="flex-1">
                          <p className="text-sm text-red-600 font-medium flex items-center gap-1">
                            <XCircle className="w-4 h-4" />
                            {item.objecion}
                          </p>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-emerald-600 flex items-center gap-1">
                            <CheckCircle2 className="w-4 h-4" />
                            {item.respuesta}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Prueba Social */}
                <div className="p-4 rounded-xl bg-gradient-to-r from-emerald-50 to-blue-50 border">
                  <Label className="font-semibold mb-3 block">Prueba Social</Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {positioning.pruebaSocial.map((item, index) => (
                      <div key={index} className="p-3 bg-white rounded-lg text-center">
                        <p className="text-sm font-medium">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        {/* Estrategia Tab */}
        <TabsContent value="estrategia" className="space-y-6">
          <motion.div variants={itemVariants}>
            <Card className="card-hover">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-primary" />
                  Estrategia de Captación
                </CardTitle>
                <CardDescription>Define tu embudo y tácticas de adquisición</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Funnel Visual */}
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-full max-w-md p-4 rounded-t-xl bg-blue-100 text-center">
                      <p className="font-semibold text-blue-700">TOFU - Awareness</p>
                      <p className="text-sm text-blue-600">Contenido orgánico, Ads, SEO</p>
                    </div>
                    <div className="w-4/5 max-w-sm p-4 bg-amber-100 text-center">
                      <p className="font-semibold text-amber-700">MOFU - Consideración</p>
                      <p className="text-sm text-amber-600">Lead Magnets, Email, Webinars</p>
                    </div>
                    <div className="w-3/5 max-w-xs p-4 bg-emerald-100 text-center">
                      <p className="font-semibold text-emerald-700">BOFU - Decisión</p>
                      <p className="text-sm text-emerald-600">Llamadas, Demos, Propuestas</p>
                    </div>
                    <div className="w-2/5 max-w-[200px] p-4 rounded-b-xl bg-purple-100 text-center">
                      <p className="font-semibold text-purple-700">Cliente</p>
                      <p className="text-sm text-purple-600">Onboarding, Entrega</p>
                    </div>
                  </div>

                  {/* Tactics */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="p-4 rounded-lg border">
                      <h4 className="font-semibold mb-2">Canales Principales</h4>
                      <ul className="text-sm space-y-1 text-muted-foreground">
                        <li>• Meta Ads (Instagram/Facebook)</li>
                        <li>• Google Ads (Search)</li>
                        <li>• LinkedIn Orgánico</li>
                        <li>• SEO Blog</li>
                      </ul>
                    </div>
                    <div className="p-4 rounded-lg border">
                      <h4 className="font-semibold mb-2">Lead Magnets</h4>
                      <ul className="text-sm space-y-1 text-muted-foreground">
                        <li>• Ebook estratégico</li>
                        <li>• Masterclass grabada</li>
                        <li>• Templates descargables</li>
                        <li>• Auditoría gratuita</li>
                      </ul>
                    </div>
                    <div className="p-4 rounded-lg border">
                      <h4 className="font-semibold mb-2">Nurturing</h4>
                      <ul className="text-sm space-y-1 text-muted-foreground">
                        <li>• Secuencia email 7 días</li>
                        <li>• Newsletter semanal</li>
                        <li>• Retargeting ads</li>
                        <li>• Casos de éxito</li>
                      </ul>
                    </div>
                    <div className="p-4 rounded-lg border">
                      <h4 className="font-semibold mb-2">Conversión</h4>
                      <ul className="text-sm space-y-1 text-muted-foreground">
                        <li>• Llamada descubrimiento</li>
                        <li>• Propuesta personalizada</li>
                        <li>• Seguimiento activo</li>
                        <li>• Cierre y onboarding</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        {/* Contenido Tab */}
        <TabsContent value="contenido" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pilares de Contenido */}
            <motion.div variants={itemVariants}>
              <Card className="card-hover h-full">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    Pilares de Contenido
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {contentPillars.map((pillar, index) => (
                    <div key={index}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium">{pillar.name}</span>
                        <span className="text-sm text-muted-foreground">{pillar.percentage}%</span>
                      </div>
                      <div className="h-3 bg-muted rounded-full overflow-hidden">
                        <div className={`h-full ${pillar.color} rounded-full`} style={{ width: `${pillar.percentage}%` }} />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </motion.div>

            {/* Calendario */}
            <motion.div variants={itemVariants}>
              <Card className="card-hover h-full">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-primary" />
                      Calendario de Contenido
                    </CardTitle>
                    <Button variant="outline" size="sm">
                      <Plus className="w-4 h-4 mr-1" />
                      Añadir
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {['Lunes', 'Miércoles', 'Viernes'].map((day, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                        <div className="w-20 text-sm font-medium">{day}</div>
                        <div className="flex-1">
                          <Badge variant="secondary" className="mr-2">Instagram</Badge>
                          <span className="text-sm text-muted-foreground">Post + Stories</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Lead Magnets */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-primary" />
                  Lead Magnets
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {leadMagnets.map((magnet, index) => (
                    <div key={index} className={`p-4 rounded-lg border ${magnet.status === 'activo' ? 'bg-emerald-50 border-emerald-100' : 'bg-gray-50 border-gray-100'}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium">{magnet.name}</p>
                          <p className="text-sm text-muted-foreground mt-1">{magnet.downloads} descargas</p>
                        </div>
                        <Badge className={magnet.status === 'activo' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}>
                          {magnet.status === 'activo' ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Automatizaciones */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-primary" />
                  ManyChat / Automatizaciones
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 rounded-lg bg-blue-50 border border-blue-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Play className="w-4 h-4 text-blue-600" />
                      <span className="font-medium text-blue-700">Flow Bienvenida</span>
                    </div>
                    <p className="text-sm text-blue-600">1,234 usuarios</p>
                  </div>
                  <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Play className="w-4 h-4 text-emerald-600" />
                      <span className="font-medium text-emerald-700">Lead Magnet</span>
                    </div>
                    <p className="text-sm text-emerald-600">456 usuarios</p>
                  </div>
                  <div className="p-4 rounded-lg bg-purple-50 border border-purple-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Play className="w-4 h-4 text-purple-600" />
                      <span className="font-medium text-purple-700">Seguimiento</span>
                    </div>
                    <p className="text-sm text-purple-600">89 usuarios</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        {/* Ads Tab */}
        <TabsContent value="ads" className="space-y-6">
          {/* Inversión Mensual */}
          <motion.div variants={itemVariants}>
            <Card className="card-hover bg-gradient-to-r from-blue-50 to-purple-50">
              <CardContent className="p-6">
                <div className="grid grid-cols-3 gap-6 text-center">
                  <div>
                    <p className="text-sm text-muted-foreground">Presupuesto Mensual</p>
                    <p className="text-3xl font-bold text-blue-700">€3,300</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Gastado</p>
                    <p className="text-3xl font-bold text-amber-600">€2,330</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Disponible</p>
                    <p className="text-3xl font-bold text-emerald-600">€970</p>
                  </div>
                </div>
                <Progress value={70.6} className="h-2 mt-4" />
              </CardContent>
            </Card>
          </motion.div>

          {/* Campañas Activas */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Play className="w-5 h-5 text-emerald-500" />
                    Campañas Activas
                  </CardTitle>
                  <Button variant="outline" size="sm">
                    <Plus className="w-4 h-4 mr-1" />
                    Nueva Campaña
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {activeCampaigns.map((campaign, index) => (
                    <div key={index} className="p-4 rounded-lg border hover:shadow-md transition-all">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-semibold">{campaign.name}</h4>
                          <Badge variant="secondary" className="mt-1">{campaign.platform}</Badge>
                        </div>
                        <Badge className="bg-emerald-100 text-emerald-700">Activa</Badge>
                      </div>
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Presupuesto</p>
                          <p className="font-medium">€{campaign.budget}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Gastado</p>
                          <p className="font-medium">€{campaign.spent}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Leads</p>
                          <p className="font-medium text-emerald-600">{campaign.leads}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Coste/Lead</p>
                          <p className="font-medium">€{(campaign.spent / campaign.leads).toFixed(2)}</p>
                        </div>
                      </div>
                      <Progress value={(campaign.spent / campaign.budget) * 100} className="h-1.5 mt-3" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Historial de Campañas */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary" />
                  Historial de Campañas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 text-sm font-medium">Campaña</th>
                      <th className="text-left py-2 text-sm font-medium">Plataforma</th>
                      <th className="text-right py-2 text-sm font-medium">Presupuesto</th>
                      <th className="text-right py-2 text-sm font-medium">Leads</th>
                      <th className="text-right py-2 text-sm font-medium">ROI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaignHistory.map((campaign, index) => (
                      <tr key={index} className="border-b hover:bg-muted/30">
                        <td className="py-3 font-medium">{campaign.name}</td>
                        <td className="py-3"><Badge variant="secondary">{campaign.platform}</Badge></td>
                        <td className="py-3 text-right">€{campaign.budget.toLocaleString()}</td>
                        <td className="py-3 text-right text-emerald-600 font-medium">{campaign.leads}</td>
                        <td className="py-3 text-right">
                          <Badge className="bg-emerald-100 text-emerald-700">{campaign.roi}x</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>
      </Tabs>
    </motion.div>
  );
}
